import { css } from '@emotion/react';
import { useContext, useEffect, useRef, useState } from 'react';
import t from 'react-translate';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';

import { config } from '@config/pendo.config.json';
import ClickableContainer from 'components/clickable-container';
import NvIcon from 'shared/components/nv-icon';
import NvTooltip from 'shared/components/nv-tooltip';
import { largeSpacing, quarterSpacing, standardSpacing, threeQuartersSpacing, tripleSpacing } from 'styles/global_defaults/scaffolding';
import { SavingRegistryContext } from 'shared/hooks/use-saving-registry';
import { SavingIndicator } from 'lecture_pages/services/question-set-context';

import { FlipCardData } from './flip-card';
import FlipCardPairRow from './flip-card-pair-row';

export type FlipCardEditModalProps = {
  cards?: FlipCardData[],
  onSave?: (cards: FlipCardData[]) => Promise<FlipCardData[]>,
  closeModal: () => void,
};

const MIN_CARDS = 3;
const MAX_CARDS = 6;

let nextTempId = 0;
const makeEmptyCard = (): FlipCardData => {
  nextTempId -= 1;
  return { id: nextTempId, front: '', back: '' };
};

const FlipCardEditModal = ({ cards, onSave }: FlipCardEditModalProps) => {
  const [draftCards, setDraftCards] = useState<FlipCardData[]>(() => {
    const seeded = cards?.length ? cards.map((card) => ({ ...card })) : [];
    while (seeded.length < MIN_CARDS) {
      seeded.push(makeEmptyCard());
    }
    return seeded;
  });

  const canAddCard = draftCards.length < MAX_CARDS;
  const canDeleteCard = draftCards.length > MIN_CARDS;
  const canReorderCard = draftCards.length > 1;

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const [savingStatus, setSavingStatus] = useState(SavingIndicator.HIDDEN_STATUS);
  const savingIndicatorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { registerSaving } = useContext(SavingRegistryContext);
  // Marks the latest autosave, so a superseded call's outcome can be ignored.
  const latestRequestIdRef = useRef(0);

  // The hide-timeout can still be pending after isSaving() allows the modal to close.
  useEffect(() => () => clearTimeout(savingIndicatorTimeoutRef.current), []);

  const autoSave = (nextCards: FlipCardData[]) => {
    if (!onSave) return;

    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;
    const isStale = () => requestId !== latestRequestIdRef.current;

    const unregister = registerSaving();
    setSavingStatus(SavingIndicator.SAVING_STATUS);
    clearTimeout(savingIndicatorTimeoutRef.current);

    const scheduleHide = () => {
      savingIndicatorTimeoutRef.current = setTimeout(() => {
        setSavingStatus(SavingIndicator.HIDDEN_STATUS);
      }, 2000);
    };

    onSave(nextCards)
      .then((savedCards) => {
        if (isStale()) return;
        setSavingStatus(SavingIndicator.SUCCESS_STATUS);
        // Swap in the real persisted ids so the next autosave updates, not duplicates.
        if (savedCards?.length) {
          setDraftCards((prev) => prev.map((card, index) => (
            savedCards[index] ? { ...card, id: savedCards[index].id } : card
          )));
        }
        scheduleHide();
      })
      .catch(() => {
        // The current call is never self-aborted, so a stale rejection is just supersession.
        if (isStale()) return;
        setSavingStatus(SavingIndicator.ERROR_STATUS);
        scheduleHide();
      })
      .finally(() => {
        unregister();
      });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = draftCards.findIndex((card) => card.id === active.id);
    const newIndex = draftCards.findIndex((card) => card.id === over.id);
    const nextCards = arrayMove(draftCards, oldIndex, newIndex);
    setDraftCards(nextCards);
    autoSave(nextCards);
  };

  const updateCard = (id: number, field: 'front' | 'back', value: string) => {
    setDraftCards((prev) => prev.map((card) => (card.id === id ? { ...card, [field]: value } : card)));
  };

  const addCard = () => {
    if (draftCards.length >= MAX_CARDS) return;
    const nextCards = [...draftCards, makeEmptyCard()];
    setDraftCards(nextCards);
    autoSave(nextCards);
  };

  const removeCard = (id: number) => {
    if (draftCards.length <= MIN_CARDS) return;
    const nextCards = draftCards.filter((card) => card.id !== id);
    setDraftCards(nextCards);
    autoSave(nextCards);
  };

  const renderSavingStatus = () => {
    switch (savingStatus) {
      case SavingIndicator.SAVING_STATUS:
        return (
          <div className='d-flex text-gray-2 bg-gray-7 saving-container align-items-center justify-content-center px-2'>
            <NvIcon icon='refresh' size='xss-smallest' className='spin' />
            <div className='ml-1 semi-bold text-small'>{t.LECTURE_PAGES.COMPONENTS.FLIP_CARD.EDIT.SAVING()}</div>
          </div>
        );
      case SavingIndicator.SUCCESS_STATUS:
        return (
          <div className='d-flex bg-gray-7 saving-container align-items-center justify-content-center px-2'>
            <NvIcon icon='check' size='xss-smallest' className='text-success' />
            <div className='ml-1 semi-bold text-small'>{t.LECTURE_PAGES.COMPONENTS.FLIP_CARD.EDIT.SAVED()}</div>
          </div>
        );
      case SavingIndicator.ERROR_STATUS:
        return (
          <div className='d-flex bg-gray-7 saving-container align-items-center justify-content-center px-2'>
            <NvIcon icon='warning' size='xss-smallest' className='text-danger' />
            <div className='ml-1 semi-bold text-small'>{t.LECTURE_PAGES.COMPONENTS.FLIP_CARD.EDIT.ERROR()}</div>
          </div>
        );
      case SavingIndicator.HIDDEN_STATUS:
      default:
        return null;
    }
  };

  const styles = css`
    gap: ${standardSpacing}px;
    padding: ${standardSpacing}px ${largeSpacing}px;

    .instructions {
      margin: 0 ${largeSpacing}px;
    }

    .add-card-button {
      margin-left: ${largeSpacing}px;
    }

    .card-list {
      gap: ${standardSpacing}px;
    }

    .saving-container {
      z-index: 5;
      position: fixed;
      top: calc(100% - ${tripleSpacing}px);
      left: 50%;
      transform: translateX(-50%);
      height: ${largeSpacing}px;
      width: 75px;
      border-radius: ${threeQuartersSpacing}px;
      box-shadow: 0px ${quarterSpacing}px ${quarterSpacing}px rgba(0, 0, 0, 0.15);
    }
  `;

  return (
    <div css={styles} className='d-flex flex-column'>
      <p className='instructions text-center text-gray-2 mb-0 text-large-body'>
        {t.LECTURE_PAGES.COMPONENTS.FLIP_CARD.EDIT.INSTRUCTIONS()}
      </p>

      <DndContext
        sensors={sensors}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={draftCards.map((card) => card.id)} strategy={verticalListSortingStrategy}>
          <div className='card-list d-flex flex-column'>
            {draftCards.map((card, index) => (
              <FlipCardPairRow
                key={card.id}
                card={card}
                index={index}
                disableDelete={!canDeleteCard}
                disableReorder={!canReorderCard}
                onChange={(field, value) => updateCard(card.id, field, value)}
                onBlur={() => autoSave(draftCards)}
                onDelete={() => removeCard(card.id)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <NvTooltip text={t.LECTURE_PAGES.COMPONENTS.FLIP_CARD.EDIT.MAX_CARDS()} enabled={!canAddCard}>
        <ClickableContainer
          onClick={addCard}
          disabled={!canAddCard}
          isFocusable={canAddCard}
          className={`add-card-button align-self-start d-flex align-items-center text-${canAddCard ? 'primary' : 'gray-4'}`}
          data-qa={config.pendo.flipCard.addCard}
        >
          <NvIcon icon='add' size='smallest' className={`mr-2 text-${canAddCard ? 'primary' : 'gray-4'}`} />
          {t.LECTURE_PAGES.COMPONENTS.FLIP_CARD.EDIT.ADD_CARD()}
        </ClickableContainer>
      </NvTooltip>

      {renderSavingStatus()}
    </div>
  );
};

export default FlipCardEditModal;
